/**
 * @fileoverview Input/output schemas for storyline-ideation-v1.
 *
 * This agent generates a single new storyline with beats and story questions
 * to add to an existing story. Output is flat (3 levels max) for Gemini
 * API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { StructurePhaseSchema, StorylinePriorityEnum } from '@/domains/story-planning/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.loreContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.seriesContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.bannedNames.schema)
  .merge(AGENT_SCHEMA_OBJECTS.projectedWordCount.schema)
  .merge(AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema)
  .merge(z.object({
    existingStorylines: z.array(z.object({
      name: z.string(),
      priority: StorylinePriorityEnum,
      summary: z.string().optional(),
    })).optional().describe('Existing storylines in the project for context.'),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

const StorylineBeatOutputSchema = z.object({
  storylinePhase: StructurePhaseSchema.describe(
    'The structural function this beat serves for THIS STORYLINE\'s internal arc.'
  ),
  globalPhase: StructurePhaseSchema.describe(
    'Where this beat falls in the OVERALL story timeline. ' +
    'For A-storylines, globalPhase always equals storylinePhase. ' +
    'For B/C storylines, globalPhase may differ.'
  ),
  order: z.number().min(0).default(0).describe(
    'Ordering index (integer >= 0) when multiple beats share the same globalPhase (0-based).'
  ),
  plotEvent: z.string().describe(
    'The concrete, observable event that occurs in the story world.'
  ),
  narrativeState: z.string().optional().describe(
    'The emotional or thematic state of this storyline thread at this moment.'
  ),
  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward the storyline\'s central objective (-1 to +1).'
  ),
});

const StoryQuestionOutputSchema = z.object({
  question: z.string().describe(
    'A dramatic question raised by this storyline (e.g., "Will Marion forgive Indy?").'
  ),
});

export const OutputSchema = z.object({
  name: z.string().describe(
    'Short, evocative name for the storyline (2-5 words).'
  ),
  summary: z.string().describe(
    'Brief description of the storyline arc (1-3 sentences).'
  ),
  priority: StorylinePriorityEnum.describe(
    'Storyline priority matching the user\'s requested type.'
  ),
  beats: z.array(StorylineBeatOutputSchema).describe(
    'Structural beats for this storyline. Beat count should match the story type — see prompt guidance.'
  ),
  storyQuestions: z.array(StoryQuestionOutputSchema).describe(
    'Dramatic questions that keep readers engaged (2-4 questions).'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
